/**
 * useGlobalSearchPrefs — localStorage-backed user preferences for the
 * top-bar Connected Search bar.
 *
 * Sprint CW-G TKT-6.
 *
 * Stores:
 *   - layout: 'panels' | 'unified'  (default 'panels')
 *   - recents: string[]             (LIFO dedupe, capped at MAX_RECENTS)
 *
 * Designed to survive page refresh; resets gracefully if localStorage
 * throws (private mode, quota) — falls back to in-memory state.
 */
import { useCallback, useEffect, useState } from 'react';

export type GlobalSearchLayout = 'panels' | 'unified';

const LAYOUT_KEY = 'cw.global_search.layout';
const RECENTS_KEY = 'cw.global_search.recents';
const MAX_RECENTS = 5;

function readLayout(): GlobalSearchLayout {
  try {
    const raw = localStorage.getItem(LAYOUT_KEY);
    if (raw === 'panels' || raw === 'unified') return raw;
  } catch {/* ignore */}
  return 'panels';
}

function readRecents(): string[] {
  try {
    const raw = localStorage.getItem(RECENTS_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (Array.isArray(parsed)) {
      return parsed.filter((v): v is string => typeof v === 'string').slice(0, MAX_RECENTS);
    }
  } catch {/* ignore */}
  return [];
}

export function useGlobalSearchPrefs() {
  const [layout, setLayoutState] = useState<GlobalSearchLayout>(readLayout);
  const [recents, setRecentsState] = useState<string[]>(readRecents);

  // Cross-tab + same-tab sync via a custom event the setters dispatch.
  useEffect(() => {
    const onUpdate = () => {
      setLayoutState(readLayout());
      setRecentsState(readRecents());
    };
    window.addEventListener('storage', onUpdate);
    window.addEventListener('cw.global_search.prefs', onUpdate as EventListener);
    return () => {
      window.removeEventListener('storage', onUpdate);
      window.removeEventListener('cw.global_search.prefs', onUpdate as EventListener);
    };
  }, []);

  const setLayout = useCallback((next: GlobalSearchLayout) => {
    setLayoutState(next);
    try {
      localStorage.setItem(LAYOUT_KEY, next);
      window.dispatchEvent(new Event('cw.global_search.prefs'));
    } catch {/* ignore */}
  }, []);

  const pushRecent = useCallback((query: string) => {
    const trimmed = query.trim();
    if (!trimmed) return;
    setRecentsState((prev) => {
      const next = [trimmed, ...prev.filter((r) => r !== trimmed)].slice(0, MAX_RECENTS);
      try {
        localStorage.setItem(RECENTS_KEY, JSON.stringify(next));
        window.dispatchEvent(new Event('cw.global_search.prefs'));
      } catch {/* ignore */}
      return next;
    });
  }, []);

  const clearRecents = useCallback(() => {
    setRecentsState([]);
    try {
      localStorage.removeItem(RECENTS_KEY);
      window.dispatchEvent(new Event('cw.global_search.prefs'));
    } catch {/* ignore */}
  }, []);

  return { layout, setLayout, recents, pushRecent, clearRecents };
}
